import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  History,
  Loader2,
  ExternalLink,
  Filter,
  Activity,
  RefreshCw,
  Coins,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { OWNER_EMAIL } from "@/components/admin/AdminShell";
import { SITE_CHANGELOG, type ChangeArea } from "@/data/site-changelog";
import { getOpsStats, getCreditSnapshots, type OpsStats } from "@/lib/admin-ops-stats.functions";


export const Route = createFileRoute("/admin/changes")({
  head: () => ({
    meta: [
      { title: "Website Changes · Admin · WeLivdIt" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminChangesPage,
});

const AREA_STYLE: Record<ChangeArea, string> = {
  Website: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  Admin: "bg-violet-500/10 text-violet-700 dark:text-violet-400",
  Backend: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  Security: "bg-rose-500/10 text-rose-700 dark:text-rose-400",
  SEO: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  Payments: "bg-primary/10 text-primary",
};

const ALL_AREAS: ChangeArea[] = ["Website", "Admin", "Backend", "Security", "SEO", "Payments"];

type CreditSnapshot = {
  id: string;
  recorded_at: string;
  batch_label: string;
  remaining_credits: number | string | null;
  total_granted: number | string | null;
  period_used: number | string | null;
  period_start: string | null;
  period_end: string | null;
  notes: string | null;
};

const num = (v: number | string | null | undefined) => (v == null ? null : Number(v));

const inr = (paise: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    paise / 100,
  );


function AdminChangesPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [area, setArea] = useState<ChangeArea | "all">("all");
  const [ops, setOps] = useState<OpsStats | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [credits, setCredits] = useState<CreditSnapshot[]>([]);

  const loadLive = useCallback(async () => {
    setOpsLoading(true);
    try {
      const [o, c] = await Promise.all([getOpsStats(), getCreditSnapshots()]);
      setOps(o as OpsStats);
      setCredits((c as CreditSnapshot[]) ?? []);
    } catch {
      /* ignore */
    } finally {
      setOpsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    void loadLive();
    const t = setInterval(() => void loadLive(), 30_000);
    return () => clearInterval(t);
  }, [authChecked, loadLive]);


  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const ok = data.session ? await hasAdminAccess(data.session) : false;
      if (!ok) {
        navigate({ to: "/auth" });
        return;
      }
      // Private page: only the owner account may view it, even among admins.
      const email = data.session?.user?.email?.toLowerCase() ?? "";
      if (email !== OWNER_EMAIL) {
        navigate({ to: "/admin" });
        return;
      }
      setAuthChecked(true);
    })();
  }, [navigate]);

  const entries = useMemo(() => {
    const sorted = [...SITE_CHANGELOG].sort((a, b) => b.at.localeCompare(a.at));
    return area === "all" ? sorted : sorted.filter((e) => e.area === area);
  }, [area]);

  const last7 = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return SITE_CHANGELOG.filter((e) => new Date(e.at).getTime() >= cutoff).length;
  }, []);

  const latestCredit = credits[0] ?? null;
  const creditsTotal = useMemo(
    () => credits.reduce((t, c) => t + (num(c.period_used) ?? 0), 0),
    [credits],
  );



  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const latest = [...SITE_CHANGELOG].sort((a, b) => b.at.localeCompare(a.at))[0];

  return (
    <section className="mx-auto max-w-5xl px-5 py-10 space-y-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <h1 className="font-display text-3xl font-semibold">Website Changes</h1>
        </div>
        <p className="mt-2 text-sm text-foreground/60">
          Every update shipped to the website and admin dashboard, newest first. Visible only to admins.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Total updates logged" value={SITE_CHANGELOG.length} />
        <StatCard label="Shipped in the last 7 days" value={last7} />
        <StatCard
          label="Most recent update"
          value={latest ? formatDistanceToNow(parseISO(latest.at), { addSuffix: true }) : "—"}
        />
      </div>

      {/* Live operations */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Live website operations</h2>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground/60">
            {ops && <span>Updated {formatDistanceToNow(parseISO(ops.checkedAt), { addSuffix: true })}</span>}
            <button
              type="button"
              onClick={() => void loadLive()}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-semibold hover:bg-muted"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${opsLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
        <p className="mt-1 text-xs text-foreground/60">Reads straight from your database, and refreshes every 30 seconds.</p>
        {!ops && opsLoading ? (
          <div className="mt-4 h-20 animate-pulse rounded-xl bg-muted" />
        ) : ops ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Paid students (all time)" value={ops.paidStudents} />
            <StatCard label="Paid this month" value={ops.paidThisMonth} />
            <StatCard label="Revenue (all time)" value={inr(ops.revenuePaise)} />
            <StatCard label="Revenue this month" value={inr(ops.revenueThisMonthPaise)} />
            <StatCard label="New leads waiting" value={ops.newLeads} />
            <StatCard label="Students not yet interview-ready" value={ops.pendingMilestones} />
            <StatCard label="Upcoming interviews" value={ops.upcomingInterviews} />
            <StatCard label="Registered accounts" value={ops.totalProfiles} />
            <StatCard label="Upcoming live sessions" value={ops.upcomingSessions} />
            <StatCard label="Database response" value={`${ops.dbLatencyMs} ms · healthy`} />
          </div>
        ) : (
          <p className="mt-4 text-sm text-foreground/60">Could not load live numbers right now.</p>
        )}
      </div>

      {/* Credit tracking */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Credit usage</h2>
        </div>
        <p className="mt-1 text-xs text-foreground/60">
          Recorded each time we ship a batch of updates, from the first build in April 2026 to today.
        </p>
        {latestCredit && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Credits remaining"
              value={num(latestCredit.remaining_credits)?.toFixed(2) ?? "—"}
            />
            <StatCard label="Used this cycle" value={num(latestCredit.period_used)?.toFixed(2) ?? "—"} />
            <StatCard label="Total granted" value={num(latestCredit.total_granted)?.toFixed(2) ?? "—"} />
          </div>
        )}
        {creditsTotal > 0 && (
          <p className="mt-3 text-sm text-foreground/75">
            Total credits used since the website started: <strong>{creditsTotal.toFixed(0)}</strong> across{" "}
            {credits.length} build cycles.
          </p>
        )}
        <div className="mt-4 space-y-2">
          {credits.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-background p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold">{c.batch_label}</span>
                <span className="text-sm font-semibold text-primary">
                  {num(c.period_used)?.toFixed(1) ?? "—"} credits
                </span>
              </div>
              {c.notes && <p className="mt-1 text-xs text-foreground/60">{c.notes}</p>}
            </div>
          ))}
        </div>
      </div>



      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground/60">
          <Filter className="h-3.5 w-3.5" /> Filter by area
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <FilterPill active={area === "all"} onClick={() => setArea("all")} label="All" />
          {ALL_AREAS.map((a) => (
            <FilterPill key={a} active={area === a} onClick={() => setArea(a)} label={a} />
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {entries.length === 0 && (
          <p className="rounded-2xl border border-border bg-card p-6 text-sm text-foreground/60">
            No updates logged in this area yet.
          </p>
        )}
        {entries.map((e) => (
          <article key={`${e.at}-${e.title}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${AREA_STYLE[e.area]}`}>
                {e.area}
              </span>
              <span className="text-xs text-foreground/60">
                {format(parseISO(e.at), "d MMM yyyy · h:mm a")} ·{" "}
                {formatDistanceToNow(parseISO(e.at), { addSuffix: true })}
              </span>
            </div>
            <h2 className="mt-2 font-display text-lg font-semibold">{e.title}</h2>
            <ul className="mt-3 space-y-1.5">
              {e.details.map((d, i) => (
                <li key={i} className="flex gap-2 text-sm text-foreground/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            {e.files && e.files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {e.files.map((f) => (
                  <code key={f} className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-foreground/60">
                    {f}
                  </code>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-muted/40 p-5">
        <h2 className="text-sm font-semibold">Full billing detail</h2>
        <p className="mt-2 text-sm text-foreground/75">
          The numbers above are recorded here after every batch of work. For invoices and a person-by-person
          breakdown, open your plans and usage page.
        </p>

        <a
          href="https://lovable.dev/settings/plans"
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          Open plans &amp; credit usage <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}

function FilterPill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full px-3 py-1.5 text-xs font-semibold transition " +
        (active ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:bg-muted")
      }
    >
      {label}
    </button>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/60">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}
